import './AssignmentsListItem.module.css';

import { Badge } from '@fluentui/react-components';
import {
  ArrowClockwiseFilled,
  ArrowClockwiseRegular,
  Checkmark20Filled,
  Class24Filled,
} from '@fluentui/react-icons';
import moment from 'moment-timezone';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TeamsIcon } from '../Icons/TeamsIcon';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { Text } from '../Text/Text';
import { AssignmentType, AssignmentsListItemProps } from './Assignments.interface';

const getProgressBarColor = (percent: number) => {
  if (percent < 30) {
    return '#d55c72';
  }

  if (percent < 66) {
    return '#fbd35a';
  }

  return '#9ac788';
};

export const AssigmnentsListItem: React.FC<AssignmentsListItemProps> = ({
  readOnly,
  onClick,
  type,
  assignmentName,
  courseName,
  dueDate,
  totalSubmissionsCount,
  pendingSubmissionsCount,
  returnedSubmissionsCount,
  onRefresh,
  onDone,
  onlyClassbook,
  isSelected = false,
}) => {
  const { t } = useTranslation();
  const percent =
    returnedSubmissionsCount === undefined || totalSubmissionsCount === undefined
      ? 0
      : (returnedSubmissionsCount / totalSubmissionsCount) * 100;

  return (
    <li
      aria-label={t('assignments:item_a11y', {
        defaultValue:
          'Eingabe öffnet Unterseite mit Details zur Aufgabe: {{assignmentName}} in {{courseName}}',
        assignmentName: assignmentName,
        courseName: courseName,
      })}
      tabIndex={0}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !readOnly) {
          onClick();
        }
      }}
      className={`assigmnentsListItem${isSelected ? ' selected' : ''}`}
      onClick={readOnly ? undefined : onClick}
      data-testid="AttachmentListItem"
      style={readOnly ? { cursor: 'default' } : {}}
    >
      <div className="iconCol">
        {onlyClassbook && <Class24Filled />}
        {!onlyClassbook && <TeamsIcon />}
      </div>
      <div className="labelCol">
        <div className="label">{assignmentName}</div>
        {dueDate && (
          <div className="dueDateLabel">
            <span>{t('due_by', { defaultValue: 'Fällig am' })}: </span>
            {moment(dueDate).format('LLL')}
          </div>
        )}
      </div>
      <div className="courseNameCol">
        <div>{courseName}</div>
        {type === AssignmentType.Assigned &&
          totalSubmissionsCount !== undefined &&
          totalSubmissionsCount !== 0 &&
          !onlyClassbook && (
            <div
              className="progressBarWrapper"
              tabIndex={0}
              aria-label={t('assignments:progressbar:status_label', {
                defaultValue: 'Zeigt den Fortschritt der Aufgabe',
              })}
            >
              <ProgressBar
                color={getProgressBarColor(percent)}
                aria-label={t('assignments:progressbar:status_label', {
                  defaultValue: 'Zeigt den Fortschritt der Aufgabe',
                })}
                completed={percent}
                progressHeight="6px"
              />
            </div>
          )}
        {type === AssignmentType.Assigned && totalSubmissionsCount === 0 && !onlyClassbook && (
          <div className="progressBarWrapper">
            {t('assignments:publish_in_progress', {
              defaultValue: 'Die Aufgabe wird gerade den Schülern zugewiesen',
            })}
          </div>
        )}
        {type === AssignmentType.Scheduled && (
          <div className="progressBarWrapper">
            <ProgressBar
              aria-label={t('assignments:progressbar:status_label', {
                defaultValue: 'Zeigt den Fortschritt der Aufgabe',
              })}
              color={getProgressBarColor(0)}
              completed={0}
              progressHeight="6px"
            />
          </div>
        )}
      </div>
      <div className="badgeCol">
        {type === AssignmentType.Draft && (
          <Badge appearance="filled" color="danger" data-testid="draftLabel">
            {t('draft', { defaultValue: 'Entwurf' })}
          </Badge>
        )}
        {type === AssignmentType.Done && (
          <Badge appearance="filled" color="success" data-testid="doneLabel">
            {t('done', { defaultValue: 'Erledigt' })}
          </Badge>
        )}
        {type === AssignmentType.Assigned &&
          totalSubmissionsCount === 0 &&
          onRefresh &&
          !onlyClassbook && (
            <button
              type="button"
              className="btnRefreshItem"
              aria-label={t('assignments:item_refresh_a11y', {
                defaultValue: 'Lädt aktuellen Status neu',
              })}
              onClick={(event) => {
                event?.stopPropagation();
                onRefresh();
              }}
            >
              <ArrowClockwiseRegular className="icon-regular" />
              <ArrowClockwiseFilled className="icon-filled" />
            </button>
          )}
        {type === AssignmentType.Assigned &&
          totalSubmissionsCount !== undefined &&
          totalSubmissionsCount !== 0 &&
          !onlyClassbook && (
            <>
              <div>
                {`${pendingSubmissionsCount} / ${totalSubmissionsCount} ${t(
                  'assignments:submission_submitted',
                  { defaultValue: 'eingereicht' },
                )}`}
              </div>
              <div>
                <Text
                  weight={
                    returnedSubmissionsCount !== undefined && returnedSubmissionsCount > 0
                      ? 'bold'
                      : 'normal'
                  }
                  color={
                    returnedSubmissionsCount !== undefined && returnedSubmissionsCount > 0
                      ? 'primary'
                      : undefined
                  }
                >
                  {`${returnedSubmissionsCount} ${t('assignments:submissions_length', {
                    defaultValue: 'zurückgegeben',
                  })}`}
                </Text>
              </div>
            </>
          )}
        {type === AssignmentType.Assigned && onlyClassbook && onDone && (
          <div
            className="assignmentIconWrapper"
            role="button"
            tabIndex={0}
            aria-label={t('assignments:done_label', {
              defaultValue: 'Markiert Aufgabe als Erledigt',
            })}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter' && onDone) {
                onDone();
              }
            }}
            onClick={(event) => {
              event?.stopPropagation();
              onDone();
            }}
          >
            <Checkmark20Filled />
          </div>
        )}
        {type === AssignmentType.Scheduled && (
          <Badge appearance="filled" color="warning" data-testid="scheduledLabel">
            {t('scheduled', { defaultValue: 'Geplant' })}
          </Badge>
        )}
      </div>
    </li>
  );
};
